import React from "react";

const BRAND = {
  navy:   "#1A3A4A",
  blue:   "#2196C9",
  gold:   "#F5A623",
  bg:     "#F0F6FA",
  text:   "#1A3A4A",
  muted:  "#5A7A8A",
  border: "#D0E4EF",
  white:  "#ffffff",
};

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{
        fontSize: 16, fontWeight: 700, color: BRAND.navy,
        borderLeft: `3px solid ${BRAND.gold}`,
        paddingLeft: 12, margin: "0 0 12px",
      }}>{title}</h2>
      <div style={{ fontSize: 14, color: BRAND.muted, lineHeight: 1.8 }}>
        {children}
      </div>
    </div>
  );
}

function Para({ children }) {
  return <p style={{ margin: "0 0 10px" }}>{children}</p>;
}

export default function PrivacyPage({ onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div style={{
        background: BRAND.navy, padding: "0 32px", height: 52,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>
            <span style={{ color: BRAND.gold }}>Re</span>
            <span style={{ color: "#fff" }}>Zi</span>
          </span>
        </div>
        {onBack && (
          <button onClick={onBack} style={{
            background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.14)",
            borderRadius: 7, padding: "6px 14px", color: "rgba(255,255,255,.75)",
            fontSize: 12, cursor: "pointer",
          }}>← Back</button>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 24px 80px" }}>

        {/* Title block */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase",
            letterSpacing: "0.1em", color: BRAND.gold, marginBottom: 8 }}>
            Legal
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: BRAND.navy, margin: "0 0 8px", letterSpacing: -0.5 }}>
            Privacy Policy
          </h1>
          <p style={{ fontSize: 13, color: BRAND.muted, margin: 0 }}>
            Last updated: March 2026 &nbsp;·&nbsp; Effective date: March 2026
          </p>
        </div>

        <div style={{
          background: BRAND.white, borderRadius: 14, padding: "36px 40px",
          border: `1px solid ${BRAND.border}`,
          boxShadow: "0 2px 12px rgba(0,0,0,.04)",
        }}>

          <Section title="1. Introduction">
            <Para>
              Hans Kruger t/a ReZi ("we", "us", "our") operates the ReZi platform ("the Service"). We are
              committed to protecting your personal information in accordance with the Protection of Personal
              Information Act 4 of 2013 (POPIA) and all applicable South African privacy legislation.
            </Para>
            <Para>
              This Privacy Policy explains what personal information we collect, how we use it, and your rights
              as a data subject.
            </Para>
          </Section>

          <Section title="2. Information Officer">
            <Para>
              Our designated Information Officer is <strong>Hans Kruger</strong>, sole proprietor trading as ReZi,
              registered with the Information Regulator of South Africa as required under POPIA.
            </Para>
            <Para>
              Contact:{" "}
              <a href="mailto:rezi.support@gmail.com" style={{ color: BRAND.blue }}>rezi.support@gmail.com</a>
            </Para>
          </Section>

          <Section title="3. Information We Collect">
            <Para>We collect and process the following categories of personal information:</Para>
            <ul style={{ paddingLeft: 20, margin: "0 0 10px" }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Account information:</strong> Name, email address, company name, and password
                (stored as a cryptographic hash).
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Form data:</strong> Vehicle registration details, ID numbers, names, addresses,
                and contact information entered by users when completing RLV and NCO forms.
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Usage data:</strong> Number of forms generated, subscription plan, and transaction history.
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>Payment data:</strong> Subscription status. Payment card details are processed
                directly by PayFast and are not stored by ReZi.
              </li>
            </ul>
          </Section>

          <Section title="4. How We Use Your Information">
            <Para>We use your personal information to:</Para>
            <ul style={{ paddingLeft: 20, margin: "0 0 10px" }}>
              <li style={{ marginBottom: 6 }}>Provide, operate, and maintain the Service.</li>
              <li style={{ marginBottom: 6 }}>Generate completed RLV and NCO PDF forms on your behalf.</li>
              <li style={{ marginBottom: 6 }}>Process subscription payments and manage your account.</li>
              <li style={{ marginBottom: 6 }}>Send transactional emails such as welcome messages and receipts.</li>
              <li style={{ marginBottom: 6 }}>Improve the Service and address technical issues.</li>
            </ul>
            <Para>
              We do not sell, rent, or share your personal information with third parties for marketing purposes.
            </Para>
          </Section>

          <Section title="5. Third-Party Data Processors">
            <Para>We use the following third-party services to operate the platform:</Para>
            <ul style={{ paddingLeft: 20, margin: "0 0 10px" }}>
              <li style={{ marginBottom: 6 }}>
                <strong>Supabase:</strong> Cloud database and authentication provider. Data is stored on
                Supabase infrastructure. Supabase operates under its own privacy and security policies.
              </li>
              <li style={{ marginBottom: 6 }}>
                <strong>PayFast:</strong> South African payment processor. PayFast processes payment
                transactions and is subject to PCI-DSS compliance standards.
              </li>
            </ul>
            <Para>
              We ensure that all third-party processors provide adequate levels of data protection.
            </Para>
          </Section>

          <Section title="6. Data Retention">
            <Para>
              Account and form data is retained for as long as your account is active. If you close your
              account or request deletion, your personal information will be removed from our systems within
              30 days, except where retention is required by law.
            </Para>
            <Para>
              Generated PDF files are stored temporarily on our servers for download and are purged
              automatically after 24 hours.
            </Para>
          </Section>

          <Section title="7. Security">
            <Para>
              We implement appropriate technical and organisational measures to protect your personal
              information against unauthorised access, loss, or disclosure. These measures include
              password hashing, HTTPS encryption in transit, and access controls on our database.
            </Para>
            <Para>
              No system is completely secure. In the event of a data breach that poses a risk to your
              rights and freedoms, we will notify you and the Information Regulator as required by POPIA.
            </Para>
          </Section>

          <Section title="8. Your Rights">
            <Para>Under POPIA, you have the right to:</Para>
            <ul style={{ paddingLeft: 20, margin: "0 0 10px" }}>
              <li style={{ marginBottom: 6 }}>Request access to the personal information we hold about you.</li>
              <li style={{ marginBottom: 6 }}>Request correction of inaccurate or incomplete information.</li>
              <li style={{ marginBottom: 6 }}>Request deletion of your personal information.</li>
              <li style={{ marginBottom: 6 }}>Object to the processing of your personal information.</li>
              <li style={{ marginBottom: 6 }}>Lodge a complaint with the Information Regulator of South Africa.</li>
            </ul>
            <Para>
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:rezi.support@gmail.com" style={{ color: BRAND.blue }}>rezi.support@gmail.com</a>.
            </Para>
          </Section>

          <Section title="9. Cookies">
            <Para>
              ReZi uses browser local storage to save your session and form drafts locally on your device.
              We do not use third-party tracking cookies or advertising cookies.
            </Para>
          </Section>

          <Section title="10. Changes to This Policy">
            <Para>
              We may update this Privacy Policy from time to time. We will notify you of material changes
              by email or by displaying a notice within the Service. Continued use of the Service after
              changes constitutes acceptance of the updated policy.
            </Para>
          </Section>

          <Section title="11. Contact Us">
            <Para>
              For any privacy-related queries or to exercise your rights, contact our Information Officer at{" "}
              <a href="mailto:rezi.support@gmail.com" style={{ color: BRAND.blue }}>rezi.support@gmail.com</a> or write to:
            </Para>
            <div style={{
              background: BRAND.bg, borderRadius: 8, padding: "14px 18px",
              fontSize: 13, lineHeight: 1.8, marginTop: 8,
            }}>
              Hans Kruger t/a ReZi<br />
              Attn: Information Officer<br />
              South Africa<br />
              rezi.support@gmail.com
            </div>
          </Section>

        </div>

        {/* Footer link */}
        <div style={{ textAlign: "center", marginTop: 32, fontSize: 13, color: BRAND.muted }}>
          <a href="/terms" style={{ color: BRAND.blue, textDecoration: "none", fontWeight: 600 }}>
            Terms of Service
          </a>
          {" "}·{" "}
          <span>© 2026 Hans Kruger t/a ReZi · ReZi</span>
        </div>

      </div>
    </div>
  );
}
